import React, {useEffect, useMemo, useState} from "react";
import {Layout, Card, Table, Tag, Button, Row, Col, Statistic, Typography, message} from "antd";
import {ReloadOutlined} from "@ant-design/icons";
import AppHeader from "../../AppHeader/AppHeader";
import ApprovalsPanel from "./ApprovalsPanel";
import ManyosSpinner from "../../ManyosSpinner/ManyosSpinner";
import {anonApi} from "../../util/anonymizerApi";

const {Content} = Layout;
const {Text} = Typography;

const RUN_STATUS_COLORS = {
    running: "processing", finished: "success",
    finished_with_errors: "warning", failed: "error"
};
const AUDIT_COLORS = {apply: "blue", restore: "green", dismiss: "default"};

function sumStat(run, key) {
    if (!run || !run.stats) return 0;
    return Object.keys(run.stats).reduce((a, t) => a + ((run.stats[t] || {})[key] || 0), 0);
}

/**
 * Cockpit: the single operations dashboard, fed directly by the anonymizer
 * engine (no cockpit-api / RabbitMQ). Pending approvals + restore on top,
 * then anonymization-run history and the recent audit trail.
 */
const Cockpit = () => {
    const [runs, setRuns] = useState([]);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            anonApi("/v1/runs", {method: "GET"}).catch(() => []),
            anonApi("/v1/audit", {method: "GET"}).catch(() => [])
        ]).then(([r, a]) => {
            setRuns(Array.isArray(r) ? r : []);
            setAudit(Array.isArray(a) ? a : []);
        }).catch((e) => message.error("Cockpit load failed: " + e.message))
          .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const stats = useMemo(() => {
        const applies = audit.filter((x) => x.action === "apply");
        const restores = audit.filter((x) => x.action === "restore");
        const fieldsAnonymized = applies.reduce((n, x) => n + Object.keys(x.fields || {}).length, 0);
        return {
            runs: runs.length,
            applied: applies.length,
            fieldsAnonymized,
            restored: restores.length
        };
    }, [runs, audit]);

    return (
        <Layout style={{minHeight: "100vh", backgroundColor: "#f7f8fa"}}>
            <AppHeader
                area="cockpit"
                extra={<Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>}
            />
            <Content style={{padding: "24px"}}>
                <ApprovalsPanel/>

                <ManyosSpinner spinning={loading}>
                    <Row gutter={16} style={{marginBottom: 16}}>
                        <Col span={6}><Card size="small"><Statistic title="Anonymization runs" value={stats.runs}/></Card></Col>
                        <Col span={6}><Card size="small"><Statistic title="Tickets anonymized" value={stats.applied}/></Card></Col>
                        <Col span={6}><Card size="small"><Statistic title="Fields anonymized" value={stats.fieldsAnonymized}/></Card></Col>
                        <Col span={6}><Card size="small"><Statistic title="Restored" value={stats.restored}/></Card></Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Card size="small" title="Anonymization Runs">
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={{pageSize: 8}}
                                    dataSource={runs}
                                    columns={[
                                        {
                                            title: "Started", dataIndex: "startedAt", width: 150,
                                            render: (v) => v ? new Date(v).toLocaleString() : "—"
                                        },
                                        {
                                            title: "Status", dataIndex: "status", width: 130,
                                            render: (s) => <Tag color={RUN_STATUS_COLORS[s] || "default"}>{s}</Tag>
                                        },
                                        {title: "Found", key: "f", width: 70, render: (_, r) => sumStat(r, "recordsFound")},
                                        {
                                            title: "Result", key: "res",
                                            render: (_, r) => {
                                                const upd = sumStat(r, "recordsUpdated");
                                                const pend = sumStat(r, "pendingChanges");
                                                const del = sumStat(r, "recordsDeleted");
                                                const parts = [];
                                                if (r.dryRun) parts.push(<Tag key="d">dry run</Tag>);
                                                if (pend) parts.push(<Tag key="p" color="orange">{pend} pending</Tag>);
                                                if (upd) parts.push(<Tag key="u" color="blue">{upd} updated</Tag>);
                                                if (del) parts.push(<Tag key="x" color="red">{del} deleted</Tag>);
                                                return parts.length ? parts : <Text type="secondary">—</Text>;
                                            }
                                        }
                                    ]}
                                />
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" title="Recent Activity (Audit Trail)">
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={{pageSize: 8}}
                                    dataSource={audit}
                                    columns={[
                                        {
                                            title: "When", dataIndex: "at", width: 150,
                                            render: (v) => v ? new Date(v).toLocaleString() : "—"
                                        },
                                        {
                                            title: "Action", dataIndex: "action", width: 90,
                                            render: (a) => <Tag color={AUDIT_COLORS[a] || "default"}>{a}</Tag>
                                        },
                                        {title: "Ticket", dataIndex: "ticket", width: 150, render: (v) => v || "—"},
                                        {title: "Record", dataIndex: "recordId"},
                                        {title: "By", dataIndex: "actor", width: 110, render: (v) => v || "—"}
                                    ]}
                                />
                            </Card>
                        </Col>
                    </Row>
                </ManyosSpinner>
            </Content>
        </Layout>
    );
};

export default Cockpit;
